'use client';
import { useEffect, useRef } from 'react';
import Link from 'next/link';
import Container from '@/components/layout/Container';
import Image from 'next/image';
import BlazeSlider from 'blaze-slider';
import 'blaze-slider/dist/blaze.css';

export default function CustomerReviews({ block, position }) {
  const entry = block?.customer_reviews?.reference?.[0];
  if (!entry?.reviews?.length) return null;

  return <ReviewsSlider entry={entry} position={position} />;
}

function ReviewsSlider({ entry, position }) {
  const { description, link_text, link, reviews, uid } = entry;
  const sliderRef = useRef(null);
  const sectionLink = link?.[0];

  useEffect(() => {
    const slider = new BlazeSlider(sliderRef.current, {
      all: {
        transitionDuration: 600,
        slidesToShow: 1,
        slideGap: '40px',
        enableAutoplay: true,
        autoplayInterval: 4000
      }
    });

    return () => slider.destroy();
  }, []);

  return (
    <section
      id={`${uid}_${position}`}
      data-name={'customer_reviews'}
      className={`customer_reviews bg-parlor-green text-white`}
    >
      <Container fullPage={true}>
        <div className="relative items-center lg:flex">
          <div
            className={`customer_reviews_left relative flex h-full flex-col justify-center border-b text-center lg:w-[35%] lg:border-r lg:border-b-0 ${link_text && sectionLink?.url ? 'py-[60px] lg:py-[125px]' : 'py-[90px] lg:py-[155px]'}`}
          >
            <div className="mx-auto">
              <div
                className={`h3 lg:h2 max-w-69 ${link_text && sectionLink?.url && 'mb-8'}`}
              >
                {description}
              </div>
              {link_text && sectionLink?.url && (
                <Link href={sectionLink.url} prefetch={false}>
                  <span className="txt-btn-secondary-white body-s lg:body-r mx-auto pb-1">
                    {link_text}
                  </span>
                </Link>
              )}
            </div>
          </div>
          <div
            ref={sliderRef}
            className={`blaze-slider blaze-slider-${position} mx-auto justify-center`}
          >
            <div className="blaze-container">
              <div className="blaze-track-container mx-auto flex px-5 pt-15 sm:px-10 lg:px-0 lg:pt-0">
                <button className="blaze-prev absolute top-[50%] -left-12.5 hidden h-10 w-10 bg-white lg:block">
                  <span className="sr-only">previous</span>
                </button>
                <ul
                  className="blaze-track relative max-w-full text-center lg:w-xl"
                  tabIndex={0}
                >
                  {reviews.map((review, index) => {
                    const icon = review.icon;
                    const reviewLink = review?.link?.[0];

                    return (
                      <li key={index} className="mx-auto self-center">
                        <Image
                          src={icon?.url}
                          width="80"
                          height="28"
                          sizes="80px"
                          alt={icon?.title.replace(/\.[^/.]+$/, '')}
                          className="mx-auto mb-9"
                        />
                        <div className="h4 lg:h3">{review.review_text}</div>
                        <div
                          className={`body-s lg:body-r mt-9 ${reviewLink?.url ? 'mb-9' : ''}`}
                        >
                          — {review.reviewer_name}
                        </div>
                        {reviewLink?.url && (
                          <Link
                            href={reviewLink.url}
                            prefetch={false}
                            className="body-r! !sm:body-l txt-btn-secondary-white pb-0.75"
                            alt={reviewLink.title}
                          >
                            {review.link_text || reviewLink.title}
                          </Link>
                        )}
                      </li>
                    );
                  })}
                </ul>
                <button className="blaze-next absolute top-[50%] -right-12.5 hidden h-10 w-10 bg-white lg:block">
                  <span className="sr-only">next</span>
                </button>
              </div>
              <div className="blaze-pagination indicators-light mt-15 flex pb-15 lg:hidden lg:pb-0"></div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}
